import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import useSignalR from "hooks/useSignalR";
import { getFetchOptions } from "utils/fetch";
import useIsDeveloper from "../useIsDeveloper";
import "./RagIndexBackfill.scss";

const SIGNALR_GROUP = "rag-index-backfill";
const RUN_ID_STORAGE_KEY = "ragIndexBackfillRunId";

const CANDIDATE_PHASE = Object.freeze({
	Pending: "Pending",
	Indexing: "Indexing",
	Indexed: "Indexed",
	Skipped: "Skipped",
	Failed: "Failed"
});

const TERMINAL_PHASES = new Set([
	CANDIDATE_PHASE.Indexed,
	CANDIDATE_PHASE.Skipped,
	CANDIDATE_PHASE.Failed
]);

const SORT_ACCESSORS = Object.freeze({
	contentId: (r) => r.courseModuleContentId,
	storedName: (r) => (r.storedName || "").toLowerCase(),
	packageType: (r) => (r.packageType || "").toLowerCase(),
	phase: (r) => r.phase || "",
	indexDurationMs: (r) => r.indexDurationMs,
	chunkCount: (r) => r.chunkCount,
	error: (r) => (r.error || "").toLowerCase()
});

const formatMs = (value) => (value == null ? "—" : `${value}ms`);

const RagIndexBackfill = () => {
	const { isLoading: isCheckingAccess, isDeveloper: allowed } = useIsDeveloper();

	const [candidates, setCandidates] = useState([]);
	const [loadError, setLoadError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isRunning, setIsRunning] = useState(false);
	const [logLines, setLogLines] = useState([]);
	const [progress, setProgress] = useState({ processed: 0, total: 0 });
	const [activeRunId, setActiveRunId] = useState(null);
	const [sortConfig, setSortConfig] = useState({ column: null, direction: "asc" });
	const [phaseFilter, setPhaseFilter] = useState("All");
	const [textFilter, setTextFilter] = useState("");
	// Wipe-all modal + in-flight state. Disabled while a backfill is running.
	const [isWipeModalOpen, setIsWipeModalOpen] = useState(false);
	const [isWiping, setIsWiping] = useState(false);
	const consoleRef = useRef(null);

	const { connection, registerHandler, onConnected } = useSignalR();

	const appendLog = useCallback((text) => {
		setLogLines((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${text}`]);
	}, []);

	const upsertCandidate = useCallback((contentId, patch) => {
		setCandidates((prev) => {
			const found = prev.some((row) => row.courseModuleContentId === contentId);
			if (found) {
				return prev.map((row) =>
					row.courseModuleContentId === contentId ? { ...row, ...patch } : row);
			}
			return prev;
		});
	}, []);

	const loadCandidates = useCallback(async () => {
		if (!allowed) {
			return;
		}
		setIsLoading(true);
		setLoadError("");
		try {
			const res = await fetch("/api/v3/RagIndexBackfill/Candidates", getFetchOptions());
			if (!res.ok) {
				setLoadError(`Failed to load candidates: ${res.status} ${res.statusText}`);
				return;
			}
			const data = await res.json();
			setCandidates(data.map((row) => ({
				...row,
				phase: CANDIDATE_PHASE.Pending,
				indexDurationMs: null,
				chunkCount: null,
				error: null
			})));
		}
		catch (err) {
			setLoadError(`Failed to load candidates: ${err.message}`);
		}
		finally {
			setIsLoading(false);
		}
	}, [allowed]);

	const wipeAll = useCallback(async () => {
		setIsWiping(true);
		try {
			const res = await fetch("/api/v3/RagIndexBackfill/AllDocuments", getFetchOptions({ method: "DELETE" }));
			if (!res.ok) {
				const body = await res.text();
				appendLog(`Wipe failed: ${res.status} ${res.statusText} — ${body}`);
				return;
			}
			const data = await res.json();
			appendLog(`Wipe complete. Deleted ${data.docsDeleted ?? 0} search documents and cleared the rag-knowledge-base container.`);
			// Reset candidate phases so the UI reflects the empty index.
			setCandidates((prev) => prev.map((row) => ({
				...row,
				phase: CANDIDATE_PHASE.Pending,
				indexDurationMs: null,
				chunkCount: null,
				error: null
			})));
			setProgress({ processed: 0, total: 0 });
			setActiveRunId(null);
		}
		catch (err) {
			appendLog(`Wipe failed: ${err.message}`);
		}
		finally {
			setIsWiping(false);
			setIsWipeModalOpen(false);
		}
	}, [appendLog]);

	const handleSort = useCallback((column) => {
		setSortConfig((prev) => {
			if (prev.column !== column) {
				return { column, direction: "asc" };
			}
			if (prev.direction === "asc") {
				return { column, direction: "desc" };
			}
			return { column: null, direction: "asc" };
		});
	}, []);

	const clearFilters = useCallback(() => {
		setPhaseFilter("All");
		setTextFilter("");
		setSortConfig({ column: null, direction: "asc" });
	}, []);

	const visibleCandidates = useMemo(() => {
		let rows = candidates;
		if (phaseFilter !== "All") {
			rows = rows.filter((r) => r.phase === phaseFilter);
		}
		const needle = textFilter.trim().toLowerCase();
		if (needle) {
			rows = rows.filter((r) =>
				(r.storedName || "").toLowerCase().includes(needle) ||
				(r.originalName || "").toLowerCase().includes(needle) ||
				(r.packageType || "").toLowerCase().includes(needle) ||
				(r.error || "").toLowerCase().includes(needle));
		}
		if (sortConfig.column) {
			const accessor = SORT_ACCESSORS[sortConfig.column];
			if (accessor) {
				const dir = sortConfig.direction === "asc" ? 1 : -1;
				rows = [...rows].sort((a, b) => {
					const av = accessor(a);
					const bv = accessor(b);
					if (av == null && bv == null) return 0;
					if (av == null) return 1;
					if (bv == null) return -1;
					if (av < bv) return -1 * dir;
					if (av > bv) return 1 * dir;
					return 0;
				});
			}
		}
		return rows;
	}, [candidates, phaseFilter, textFilter, sortConfig]);

	const failedStoredNames = useMemo(
		() => candidates.filter((c) => c.phase === CANDIDATE_PHASE.Failed).map((c) => c.storedName),
		[candidates]
	);

	useEffect(() => {
		loadCandidates();
	}, [loadCandidates]);

	useEffect(() => {
		if (consoleRef.current) {
			consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
		}
	}, [logLines]);

	useEffect(() => {
		if (!connection || !allowed) {
			return undefined;
		}

		onConnected(async (conn) => {
			await conn.invoke("JoinGroupAsync", SIGNALR_GROUP);
		});
		connection.onreconnected(async () => {
			await connection.invoke("JoinGroupAsync", SIGNALR_GROUP);
		});

		const unregisterStarted = registerHandler("RagIndexBackfillRunStarted", SIGNALR_GROUP, (message) => {
			const payload = message.payload || {};
			setActiveRunId(payload.runId);
			setIsRunning(true);
			setProgress({ processed: 0, total: payload.totalCandidates ?? 0 });
			appendLog(`Run ${payload.runId} started — ${payload.totalCandidates ?? 0} candidate(s).`);
			setCandidates((prev) => prev.map((row) => ({
				...row,
				phase: CANDIDATE_PHASE.Pending,
				indexDurationMs: null,
				chunkCount: null,
				error: null
			})));
		});

		const unregisterCandidate = registerHandler("RagIndexBackfillCandidateChanged", SIGNALR_GROUP, (message) => {
			const payload = message.payload || {};
			upsertCandidate(payload.courseModuleContentId, {
				phase: payload.phase,
				indexDurationMs: payload.indexDurationMs,
				chunkCount: payload.chunkCount,
				error: payload.error
			});
			if (TERMINAL_PHASES.has(payload.phase)) {
				setProgress({ processed: payload.processed, total: payload.total });
			}
			const stamp = TERMINAL_PHASES.has(payload.phase) ? `${payload.processed ?? "?"}/${payload.total ?? "?"}` : "";
			appendLog(`${payload.storedName} → ${payload.phase}${stamp ? ` (${stamp})` : ""}${payload.error ? `: ${payload.error}` : ""}`);
		});

		const unregisterCompleted = registerHandler("RagIndexBackfillRunCompleted", SIGNALR_GROUP, (message) => {
			const payload = message.payload || {};
			setIsRunning(false);
			appendLog(
				`Run ${payload.runId} complete: ${payload.indexed ?? 0} indexed, ` +
				`${payload.failed ?? 0} failed, ${payload.skipped ?? 0} skipped, ` +
				`${payload.durationMs ?? 0}ms.`);
			sessionStorage.removeItem(RUN_ID_STORAGE_KEY);
		});

		return () => {
			unregisterStarted();
			unregisterCandidate();
			unregisterCompleted();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connection, allowed, onConnected, registerHandler]);

	const startBackfill = useCallback(async (subsetStoredNames = []) => {
		if (isRunning) {
			return;
		}
		setIsRunning(true);
		setLogLines([]);
		try {
			const options = getFetchOptions({
				method: "POST",
				body: { storedNames: subsetStoredNames }
			});
			const response = await fetch("/api/v3/RagIndexBackfill/Runs", options);
			if (!response.ok) {
				appendLog(`Run failed to start: ${response.status} ${response.statusText}`);
				setIsRunning(false);
				return;
			}
			const payload = await response.json();
			if (!payload.runId) {
				appendLog("Run failed to start: server did not return a runId.");
				setIsRunning(false);
				return;
			}
			sessionStorage.setItem(RUN_ID_STORAGE_KEY, payload.runId);
			setActiveRunId(payload.runId);
			appendLog(`Run ${payload.runId} queued. Awaiting SignalR updates...`);
		}
		catch (err) {
			appendLog(`Run errored: ${err.message}`);
			setIsRunning(false);
		}
	}, [appendLog, isRunning]);

	const abortBackfill = useCallback(async () => {
		const runId = activeRunId || sessionStorage.getItem(RUN_ID_STORAGE_KEY);
		if (!runId) {
			return;
		}
		try {
			await fetch(`/api/v3/RagIndexBackfill/Runs/${runId}`, getFetchOptions({ method: "DELETE" }));
			appendLog("Cancel requested. Handlers will stop after their current step.");
		}
		catch (err) {
			appendLog(`Cancel request failed: ${err.message}`);
		}
	}, [activeRunId, appendLog]);

	if (isCheckingAccess) {
		return null;
	}
	if (!allowed) {
		return <Navigate to="/dashboard" replace />;
	}

	const SortableHeader = ({ column, label }) => {
		const active = sortConfig.column === column;
		const indicator = active ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : "";
		return (
			<th onClick={() => handleSort(column)} style={{ cursor: "pointer", userSelect: "none" }}>
				{label}{indicator}
			</th>
		);
	};

	return (
		<div className="rag-index-backfill">
			<header className="rag-index-backfill__header">
				<h1>RAG Index Backfill</h1>
				<p>
					Re-indexes the RAG knowledge-base from existing transcripts. Each candidate's
					<code>transcriptionResult.json</code> is chunked, embedded, and upserted into
					the <code>-rag-knowledge-base-index</code> for the current environment.
				</p>
			</header>

			<section className="rag-index-backfill__controls">
				<button type="button" onClick={() => startBackfill()} disabled={isRunning || isLoading || candidates.length === 0}>
					{isRunning ? "Running..." : "Start backfill"}
				</button>
				<button
					type="button"
					onClick={() => startBackfill(failedStoredNames)}
					disabled={isRunning || isLoading || failedStoredNames.length === 0}
					title="Start a new run containing only the candidates that failed"
				>
					Re-run failed{failedStoredNames.length ? ` (${failedStoredNames.length})` : ""}
				</button>
				<button type="button" onClick={abortBackfill} disabled={!isRunning}>
					Abort
				</button>
				<button type="button" onClick={loadCandidates} disabled={isRunning || isLoading}>
					Reload list
				</button>
				<button
					type="button"
					className="rag-index-backfill__danger"
					onClick={() => setIsWipeModalOpen(true)}
					disabled={isRunning || isLoading || isWiping}
				>
					{isWiping ? "Wiping..." : "Wipe all documents"}
				</button>
				<span className="rag-index-backfill__progress">
					{progress.total > 0 ? `${progress.processed} / ${progress.total}` : `${candidates.length} candidate(s)`}
				</span>
				{activeRunId && (
					<span className="rag-index-backfill__run-id" title={activeRunId}>
						Run: {activeRunId.slice(0, 8)}
					</span>
				)}
			</section>

			{isWipeModalOpen && (
				<div className="rag-index-backfill__modal-backdrop" role="dialog" aria-modal="true">
					<div className="rag-index-backfill__modal">
						<h2>Wipe all RAG documents?</h2>
						<p>
							This deletes every document from
							<code> {`{search-service-name}-rag-knowledge-base-index`} </code>
							and every blob from the <code>rag-knowledge-base</code> container.
							Transcripts in the transcripts container are not affected.
						</p>
						<p>
							Cannot be undone. The next backfill run will re-create the documents from
							the existing transcripts.
						</p>
						<div className="rag-index-backfill__modal-buttons">
							<button type="button" onClick={() => setIsWipeModalOpen(false)} disabled={isWiping}>
								Cancel
							</button>
							<button
								type="button"
								className="rag-index-backfill__danger"
								onClick={wipeAll}
								disabled={isWiping}
							>
								{isWiping ? "Wiping..." : "Wipe Everything"}
							</button>
						</div>
					</div>
				</div>
			)}

			{loadError && <div className="rag-index-backfill__error">{loadError}</div>}

			<section className="rag-index-backfill__filters" style={{ display: "flex", gap: "1em", alignItems: "center", flexWrap: "wrap", marginBottom: "0.5em" }}>
				<label>
					Phase:{" "}
					<select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
						<option value="All">All</option>
						{Object.values(CANDIDATE_PHASE).map((p) => (
							<option key={p} value={p}>{p}</option>
						))}
					</select>
				</label>
				<label>
					Search:{" "}
					<input
						type="text"
						value={textFilter}
						onChange={(e) => setTextFilter(e.target.value)}
						placeholder="Stored name, package, error..."
						style={{ minWidth: "20em" }}
					/>
				</label>
				<button type="button" onClick={clearFilters}>Clear filters</button>
				<span>Showing {visibleCandidates.length} of {candidates.length}</span>
			</section>

			<section className="rag-index-backfill__table-wrapper">
				<table className="rag-index-backfill__table">
					<thead>
						<tr>
							<th>Run</th>
							<SortableHeader column="contentId" label="Content ID" />
							<SortableHeader column="storedName" label="Stored Name" />
							<SortableHeader column="packageType" label="Package" />
							<SortableHeader column="phase" label="Phase" />
							<SortableHeader column="indexDurationMs" label="Index ms" />
							<SortableHeader column="chunkCount" label="Chunks" />
							<SortableHeader column="error" label="Error" />
						</tr>
					</thead>
					<tbody>
						{isLoading && (
							<tr><td colSpan={8}>Loading...</td></tr>
						)}
						{!isLoading && candidates.length === 0 && (
							<tr><td colSpan={8}>No candidates found.</td></tr>
						)}
						{!isLoading && candidates.length > 0 && visibleCandidates.length === 0 && (
							<tr><td colSpan={8}>No rows match the current filters.</td></tr>
						)}
						{visibleCandidates.map((row) => {
							const phase = row.phase || CANDIDATE_PHASE.Pending;
							return (
								<tr key={row.courseModuleContentId} className={`rag-index-backfill__row rag-index-backfill__row--${phase.toLowerCase()}`}>
									<td>
										<button
											type="button"
											onClick={() => startBackfill([row.storedName])}
											disabled={isRunning || isLoading}
											title="Index just this course"
										>
											Run
										</button>
									</td>
									<td>{row.courseModuleContentId}</td>
									<td title={row.originalName}>{row.storedName}</td>
									<td>{row.packageType}</td>
									<td>{phase}</td>
									<td>{formatMs(row.indexDurationMs)}</td>
									<td>{row.chunkCount != null ? row.chunkCount : "—"}</td>
									<td className="rag-index-backfill__error-cell">{row.error || ""}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</section>

			<section className="rag-index-backfill__console-wrapper">
				<h2>Console</h2>
				<pre ref={consoleRef} className="rag-index-backfill__console">
					{logLines.join("\n")}
				</pre>
			</section>
		</div>
	);
};

export default RagIndexBackfill;
